/**
 * AIReadinessChip — semantic AI state label.
 *
 * Replaces raw ProcessingStatus text everywhere in the Hub with
 * human-readable, AI-native language that explains what the system
 * is doing and what it knows.
 *
 * Two variants:
 *   "row"  — compact pill: [dot] [label]  right-aligned in DocRow
 *             filename line. Short labels only.
 *   "full" — standard pill: [dot] [label] [· sublabel]  used in
 *             HubDetail sticky header.
 *
 * Processing states (uploading, indexing, classifying) animate
 * with a slow pulse to signal active AI work. Terminal states
 * are static.
 *
 * Semantic mapping:
 *   uploading        → "Ingesting"   (indigo)
 *   indexing         → "Parsing"     (indigo)
 *   classifying      → "Understanding…" (violet, pulsing)
 *   ready + conf≥.85 → "AI Ready"   (emerald)
 *   ready + conf≥.60 → "AI Ready"   (amber — with sublabel "review suggested")
 *   ready + conf<.60 → "Low confidence" (red)
 *   ready + no conf  → "AI Ready"   (teal)
 *   verified         → "Verified"   (emerald, solid)
 *   limited          → "Text not indexed" (amber — structure only)
 *   failed_*         → "Failed"     (red)
 */

import { makeStyles } from "@fluentui/react-components";
import type React from "react";
import { fonts, inkColors } from "../tokens";
import { aiColors } from "../tokens";
import type { DocumentRow } from "../types";

export interface AIReadinessChipProps {
  row: Pick<DocumentRow, "processing_status" | "source_classification" | "verified" | "low">;
  variant?: "row" | "full";
}

interface ReadinessInfo {
  label: string;
  sublabel?: string;
  color: string;
  bgColor: string;
  pulsing: boolean;
}

function getReadiness(row: AIReadinessChipProps["row"]): ReadinessInfo {
  // Verified always wins — signed-off supersedes every other state.
  if (row.verified) {
    return {
      label: "Verified",
      sublabel: "signed off",
      color: aiColors.trustVerified,
      bgColor: "#ECFDF5",
      pulsing: false,
    };
  }

  const conf = row.source_classification?.source_doctype_confidence ?? null;

  switch (row.processing_status) {
    case "uploading":
      return {
        label: "Ingesting",
        sublabel: "uploading file",
        color: aiColors.thinking,
        bgColor: "#EEF2FF",
        pulsing: true,
      };
    case "indexing":
      return {
        label: "Parsing",
        sublabel: "building index",
        color: aiColors.thinking,
        bgColor: "#EEF2FF",
        pulsing: true,
      };
    case "classifying":
      return {
        label: "Understanding…",
        sublabel: "AI classifying",
        color: aiColors.classifying,
        bgColor: "#F5F3FF",
        pulsing: true,
      };
    case "ready": {
      if (typeof conf === "number") {
        if (conf >= 0.85) {
          return {
            label: "AI Ready",
            sublabel: "high confidence",
            color: aiColors.evidenceHigh,
            bgColor: "#ECFDF5",
            pulsing: false,
          };
        }
        if (conf >= 0.6) {
          return {
            label: "AI Ready",
            sublabel: "review suggested",
            color: aiColors.evidenceMid,
            bgColor: "#FFFBEB",
            pulsing: false,
          };
        }
        return {
          label: "Low confidence",
          sublabel: "please review",
          color: aiColors.evidenceLow,
          bgColor: "#FEF2F2",
          pulsing: false,
        };
      }
      return {
        label: "AI Ready",
        sublabel: "classified",
        color: aiColors.understood,
        bgColor: "#F0FDFA",
        pulsing: false,
      };
    }
    case "limited":
      return {
        label: "Text not indexed",
        sublabel: "structure only",
        color: aiColors.evidenceMid,
        bgColor: "#FFFBEB",
        pulsing: false,
      };
    case "failed_upload":
      return {
        label: "Upload failed",
        sublabel: undefined,
        color: aiColors.evidenceLow,
        bgColor: "#FEF2F2",
        pulsing: false,
      };
    case "failed_processing":
      return {
        label: "Processing failed",
        sublabel: undefined,
        color: aiColors.evidenceLow,
        bgColor: "#FEF2F2",
        pulsing: false,
      };
  }
}

// Inject pulse keyframes once.
const STYLE_ID = "ai-readiness-chip-keyframes";
if (typeof document !== "undefined" && !document.getElementById(STYLE_ID)) {
  const el = document.createElement("style");
  el.id = STYLE_ID;
  el.textContent = `
    @keyframes ai-chip-pulse {
      0%, 100% { opacity: 1; }
      50%       { opacity: 0.35; }
    }
  `;
  document.head.appendChild(el);
}

const useStyles = makeStyles({
  chip: {
    display: "inline-flex",
    alignItems: "center",
    columnGap: "4px",
    borderRadius: "9999px",
    flexShrink: 0,
  },
  chipRow: {
    padding: "1px 5px",
    // border: none for row variant — background signals the state
    border: "1px solid transparent",
  },
  chipFull: {
    padding: "2px 7px",
    border: "1px solid transparent",
  },
  dot: {
    width: "5px",
    height: "5px",
    borderRadius: "50%",
    flexShrink: 0,
  },
  dotPulsing: {
    animationName: "ai-chip-pulse",
    animationDuration: "1.8s",
    animationTimingFunction: "ease-in-out",
    animationIterationCount: "infinite",
  },
  label: {
    fontFamily: fonts.mono,
    fontWeight: 700,
    letterSpacing: "0.3px",
    whiteSpace: "nowrap",
  },
  labelRow: { fontSize: "8px" },
  labelFull: { fontSize: "9px" },
  sublabel: {
    fontSize: "8px",
    fontFamily: fonts.mono,
    opacity: 0.7,
    whiteSpace: "nowrap",
    letterSpacing: "0.2px",
  },
});

export const AIReadinessChip: React.FC<AIReadinessChipProps> = ({ row, variant = "row" }) => {
  const styles = useStyles();
  const info = getReadiness(row);

  return (
    <span
      className={`${styles.chip} ${variant === "row" ? styles.chipRow : styles.chipFull}`}
      style={{
        backgroundColor: info.bgColor,
        borderColor: `${info.color}33`,
        color: info.color,
      }}
      title={info.sublabel ?? info.label}
      aria-label={`AI status: ${info.label}${info.sublabel ? ` — ${info.sublabel}` : ""}`}
    >
      <span
        className={`${styles.dot} ${info.pulsing ? styles.dotPulsing : ""}`}
        style={{ backgroundColor: info.color }}
        aria-hidden="true"
      />
      <span className={`${styles.label} ${variant === "row" ? styles.labelRow : styles.labelFull}`}>{info.label}</span>
      {variant === "full" && info.sublabel && <span className={styles.sublabel}>· {info.sublabel}</span>}
    </span>
  );
};

export default AIReadinessChip;
